<template>
  <div class="pwd-page">
    <!-- Custom Header -->
    <header class="pwd-header">
      <button class="pwd-header__back" @click="$router.go(-1)">
        <van-icon name="arrow-left" size="20" color="#ffffff" />
      </button>
      <h1 class="pwd-header__title">{{ $lang.passwordLogin_title || 'CHANGE PASSWORD' }}</h1>
      <div class="pwd-header__spacer"></div>
    </header>
    <div class="pwd-header-pad"></div>

    <div class="pwd-form">
      <!-- Old Password -->
      <div class="pwd-field">
        <div class="pwd-field__input" :class="{ 'pwd-field__input--error': error }">
          <div class="pwd-field__icon">
            <van-icon name="lock" size="18" color="var(--vk-t2)" />
          </div>
          <input
            v-model="oldLoginPwd"
            :type="isSee ? 'text' : 'password'"
            :placeholder="$lang.passwordLogin_txt5 || 'Enter the old password'"
            class="pwd-field__text"
          />
          <button class="pwd-field__toggle" @click="isSee = !isSee">
            <van-icon :name="isSee ? 'eye-o' : 'closed-eye'" size="18" :color="isSee ? '#ff5a1f' : '#4b5563'" />
          </button>
        </div>
        <p v-if="errorText" class="pwd-field__error">{{ errorText }}</p>
      </div>

      <!-- New Password -->
      <div class="pwd-field">
        <div class="pwd-field__input" :class="{ 'pwd-field__input--error': error2 }">
          <div class="pwd-field__icon">
            <van-icon name="lock" size="18" color="var(--vk-t2)" />
          </div>
          <input
            v-model="newLoginPwd"
            :type="isSee2 ? 'text' : 'password'"
            :placeholder="$lang.passwordLogin_txt6 || 'Enter the new password'"
            class="pwd-field__text"
          />
          <button class="pwd-field__toggle" @click="isSee2 = !isSee2">
            <van-icon :name="isSee2 ? 'eye-o' : 'closed-eye'" size="18" :color="isSee2 ? '#ff5a1f' : '#4b5563'" />
          </button>
        </div>
        <p v-if="errorText2" class="pwd-field__error">{{ errorText2 }}</p>
      </div>

      <!-- Confirm Password -->
      <div class="pwd-field">
        <div class="pwd-field__input" :class="{ 'pwd-field__input--error': error3 }">
          <div class="pwd-field__icon">
            <van-icon name="passed" size="18" color="var(--vk-t2)" />
          </div>
          <input
            v-model="confirmPwd"
            :type="isSee3 ? 'text' : 'password'"
            :placeholder="$lang.pwdConfirm_ph || 'Confirm new password'"
            class="pwd-field__text"
          />
          <button class="pwd-field__toggle" @click="isSee3 = !isSee3">
            <van-icon :name="isSee3 ? 'eye-o' : 'closed-eye'" size="18" :color="isSee3 ? '#ff5a1f' : '#4b5563'" />
          </button>
        </div>
        <p v-if="errorText3" class="pwd-field__error">{{ errorText3 }}</p>
      </div>

      <p class="pwd-form__tip">{{ $lang.passwordLogin_txt4 }}</p>

      <button class="pwd-form__submit" :disabled="submitting" @click="submit">
        {{ submitting ? ($lang.common_loading || 'Loading...') : ($lang.Enviar || 'Submit') }}
      </button>
    </div>
  </div>
</template>

<script>
import { ChangeLoginPwd } from '@/api/common'
import md5 from 'md5'
import { ensureLogin as ensureMineLogin, failMessage, handleNeedLogin, isSafeWriteSuccess } from './safeFlow'

export default {
  name: 'PasswordLogin',
  data() {
    return {
      oldLoginPwd: '',
      newLoginPwd: '',
      confirmPwd: '',
      error: false,
      error2: false,
      error3: false,
      errorText: '',
      errorText2: '',
      errorText3: '',
      isSee: false,
      isSee2: false,
      isSee3: false,
      submitting: false
    }
  },
  mounted() {
    this.ensureLogin()
  },
  methods: {
    ensureLogin() {
      return ensureMineLogin(this)
    },
    async submit() {
      if (this.submitting) return
      if (!this.ensureLogin()) return
      this.error = false
      this.error2 = false
      this.error3 = false
      this.errorText = ''
      this.errorText2 = ''
      this.errorText3 = ''

      if (!this.oldLoginPwd) {
        this.error = true
        this.errorText = this.$lang.passwordLogin_txt5 || 'Enter the old password'
      }
      if (!this.newLoginPwd) {
        this.error2 = true
        this.errorText2 = this.$lang.passwordLogin_txt6 || 'Enter the new password'
      } else if (this.oldLoginPwd === this.newLoginPwd) {
        this.error2 = true
        this.errorText2 = this.$lang.passwordLogin_txt7 || 'The new password is the same as the old one'
      }
      if (!this.confirmPwd) {
        this.error3 = true
        this.errorText3 = this.$lang.pwdConfirm_ph || 'Confirm new password'
      } else if (this.newLoginPwd !== this.confirmPwd) {
        this.error3 = true
        this.errorText3 = this.$lang.pwdConfirm_mismatch || 'Passwords do not match'
      }

      if (this.error || this.error2 || this.error3) return

      this.submitting = true
      try {
        const data = await ChangeLoginPwd({
          oldLoginPwd: md5(this.oldLoginPwd),
          newLoginPwd: md5(this.newLoginPwd)
        })
        if (handleNeedLogin(this, data)) return
        if (isSafeWriteSuccess(data)) {
          this.$jumpTo('/passwordSuccess')
        } else {
          this.$toast({ message: failMessage(data, 'Password update failed'), icon: 'cross' })
        }
      } catch (e) {
        this.$toast({ message: failMessage(e, '登录密码更新失败，请稍后重试。'), icon: 'cross' })
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>

<style lang="less" scoped>
@bg: @vk-bg;       /* 页面底 */
@card: @vk-surface;/* 卡片面 */
@neon: @brand;     /* 绿→火焰橙(强调/激活) */
@muted: @vk-t2;    /* 次文字 */
@border: #3f4451;  /* 中性描边 */

.pwd-page {
  min-height: 100vh;
  background: @bg;
}

.pwd-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  height: 50px;
  background: @bg;
  z-index: 999;

  &__back {
    background: none;
    border: none;
    cursor: pointer;
    padding: 8px;
  }

  &__title {
    font-size: var(--vk-fs-md);
    font-weight: 700;
    color: #ffffff;
    text-transform: uppercase;
    letter-spacing: 1px;
  }

  &__spacer {
    width: 36px;
  }
}

.pwd-header-pad {
  height: 50px;
}

.pwd-form {
  padding: 24px 16px;

  &__tip {
    font-size: var(--vk-fs-xs);
    color: @muted;
    margin: 8px 4px 24px;
    line-height: 1.6;
  }

  &__submit {
    width: 100%;
    background: @neon;
    color: @bg;
    border: none;
    border-radius: 10px;
    padding: 14px;
    font-size: var(--vk-fs-base);
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1px;
    cursor: pointer;
    box-shadow: 0 4px 16px rgba(255, 90, 31, 0.2);
    transition: transform 0.1s;

    &:active {
      transform: scale(0.98);
    }
  }
}

.pwd-field {
  margin-bottom: 16px;

  &__input {
    display: flex;
    align-items: center;
    background: @card;
    border: 1px solid @border;
    border-radius: 10px;
    padding: 0 14px;
    height: 50px;
    transition: border-color 0.2s, box-shadow 0.2s;

    &:focus-within {
      border-color: @neon;
      box-shadow: 0 0 0 2px rgba(255, 90, 31, 0.1);
    }

    &--error {
      border-color: var(--vk-danger);
    }
  }

  &__icon {
    margin-right: 12px;
    display: flex;
    align-items: center;
  }

  &__text {
    flex: 1;
    background: transparent;
    border: none;
    color: #ffffff;
    font-size: var(--vk-fs-base);
    outline: none;

    &::placeholder {
      color: #4b5563;
    }
  }

  &__toggle {
    background: none;
    border: none;
    cursor: pointer;
    padding: 4px;
    display: flex;
    align-items: center;
  }

  &__error {
    font-size: var(--vk-fs-xs);
    color: var(--vk-danger);
    margin-top: 6px;
    margin-left: 4px;
  }
}

@media (min-width: 769px) {
  .pwd-page {
    max-width: var(--vk-pc-col);
    margin: 0 auto;
  }
}
</style>
